import { useCallback, useEffect, useState } from "react";

import { AddContactForm, IInvitationContactEmail, IInvitationContactPhone } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/AddContactForm/AddContactForm";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";

import styles from "./ContactForm.module.scss";

interface IContactFormProps {
    email?: IInvitationContactEmail;
    emails: IInvitationContactEmail[];
    onEmailChange?: (email?: IInvitationContactEmail) => void;
    phone?: IInvitationContactPhone;
    phones: IInvitationContactPhone[];
    onPhoneChange?: (phone?: IInvitationContactPhone) => void;
    onAddContact?: (isContactNew: boolean) => void;
}

function ContactForm(props: IContactFormProps): JSX.Element {
    const validationHelper = new ValidationHelper();

    const { getText } = useTranslation();
    const [addContact, setAddContact] = useState<boolean>(false);

    const isEmailChecked = useCallback(
        (contact: IInvitationContactEmail) => contact.address === props.email?.address,
        [props.email]
    );

    const isPhoneChecked = useCallback(
        (contact: IInvitationContactPhone) => contact.number === props.phone?.number,
        [props.phone]
    );

    const handleAddContactChange = (): void => {
        setAddContact(true);

        const contactPointId = cacheStore.GetEmailContactPointTypeLookups().at(0).LookupID;

        props.onEmailChange?.({ contactPointId, address: "" });
        props.onPhoneChange?.();
    };

    const handleEmailContactChange = (email: IInvitationContactEmail): void => {
        setAddContact(false);

        props.onEmailChange?.(email);
        props.onPhoneChange?.();
    };

    const handlePhoneContactChange = (phone: IInvitationContactPhone): void => {
        setAddContact(false);

        props.onPhoneChange?.(phone);
        props.onEmailChange?.();
    };

    const renderEmailContact = (email: IInvitationContactEmail, index: number): JSX.Element => {
        const displayContactType = cacheStore.GetLocalizedValueByLookupId(email.contactPointId);
        const displayContact = MyleHelper.ReplaceEmptyString(email.address);

        const label = `${displayContact} (${displayContactType})`;

        return (
            <div key={`contact-${index}`} className="radio">
                <label>
                    <input
                        type="radio"
                        name="contact"
                        checked={isEmailChecked(email)}
                        onChange={() => handleEmailContactChange(email)}
                    />

                    {label}
                </label>
            </div>
        );
    };

    const renderPhoneContact = (phone: IInvitationContactPhone, index: number): JSX.Element => {
        const validationRule = validationHelper.getPhoneNumberValidationRule(phone.countryLookupId, null); // no need for region when checking for phone number format?
        const displayFormatSelector = validationRule?.DisplayFormatSelectorPatern ?? "";
        const displayFormatSubstitution = validationRule?.DisplayFormatSubstitution ?? "";
        const displayContactType = `${MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(phone.contactPointId))} - ${MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(phone.phoneTypeId))}`;

        const label = `${validationHelper.getFormatedString(phone.number, displayFormatSelector, displayFormatSubstitution)} (${displayContactType})`;

        return (
            <div key={`contact-${index}`} className="radio">
                <label>
                    <input
                        type="radio"
                        name="contact"
                        checked={isPhoneChecked(phone)}
                        onChange={() => handlePhoneContactChange(phone)}
                    />

                    {label}
                </label>
            </div>
        );
    };

    /**
    * Trigger onAddContact event
    * Effect triggers onAddContact event every time when choosing or trying to create new contact
    */
    useEffect(
        () => {
            props.onAddContact?.(addContact);
        },
        [addContact, props.onAddContact]
    );

    return (
        <>
            <h4 className={styles.contactFormHeading}>
                {getText("PortalInvitation_InviteToPortalLabel")}
            </h4>

            <form name="contactForm" className={styles.contactForm}>
                <h4 className={styles.contactFormSubHeading}>
                    {getText("PortalInvitation_ChoosePatientContactLabel")}
                </h4>

                {props.emails.map(renderEmailContact)}

                {cacheStore.ClinicSettings_Portal.EnablePortalV2 && props.phones.map(renderPhoneContact)}

                <div className="radio">
                    <label>
                        <input
                            type="radio"
                            name="contact"
                            checked={addContact}
                            onChange={handleAddContactChange}
                        /> {getText("PortalInvitation_AddPatientContactLabel")}
                    </label>
                </div>

                {addContact && (
                    <AddContactForm
                        email={props.email}
                        phone={props.phone}
                        onEmailChange={props.onEmailChange}
                        onPhoneChange={props.onPhoneChange}
                    />
                )}
            </form>
        </>
    );
}

export { ContactForm };
